import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Mic2, Music2, Waves, Radio, Wind } from "lucide-react";

const VOICES = [
    { id: "hyped",  name_fr: "Narrateur hypé",     name_en: "Hyped narrator",     preset: "high-energy, punchy, upbeat" },
    { id: "story",  name_fr: "Conteur",            name_en: "Storyteller",        preset: "warm, cinematic, immersive" },
    { id: "asmr",   name_fr: "ASMR",               name_en: "ASMR",               preset: "soft, close-mic, whisper" },
    { id: "cartoon",name_fr: "Cartoon",            name_en: "Cartoon",            preset: "cheerful, exaggerated, playful" },
    { id: "doc",    name_fr: "Documentaire",       name_en: "Documentary",        preset: "authoritative, calm, serious" },
    { id: "susp",   name_fr: "Suspense",           name_en: "Suspense",           preset: "low pitch, dramatic pause" },
];

const MOODS = [
    { id: "lo-fi",   label: "Lo-Fi",      bpm: 78,  color: "#0099FF" },
    { id: "epic",    label: "Epic",       bpm: 140, color: "#FF4D00" },
    { id: "comedy",  label: "Comedy",     bpm: 110, color: "#FFB800" },
    { id: "tense",   label: "Tense",      bpm: 95,  color: "#10B981" },
];

export default function VoiceAudio() {
    const { lang } = useApp();
    const [voice, setVoice] = useState("hyped");
    const [mood, setMood] = useState("epic");
    const [sfx, setSfx] = useState({ whoosh: true, ding: true, boom: false, laugh: false });

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-5">
                <div>
                    <div className="overline">08 · Voice & Audio</div>
                    <h2 className="font-display text-2xl font-black uppercase mt-1">{lang === "fr" ? "Studio voix + audio" : "Voice + audio studio"}</h2>
                    <p className="text-sm text-slate-400 mt-2">{lang === "fr" ? "Choisis un preset voix, une ambiance musicale et empile les SFX." : "Pick a voice preset, a music mood, and stack SFX."}</p>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2"><Mic2 className="w-3.5 h-3.5" /> {lang === "fr" ? "Preset voix" : "Voice preset"}</label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                        {VOICES.map((v) => (
                            <button key={v.id} onClick={() => setVoice(v.id)}
                                className={`p-3 rounded border text-left kinetic ${voice === v.id ? "border-[#FF4D00] bg-[#FF4D00]/10" : "border-[#253046] hover:border-[#3B4B6C]"}`}>
                                <div className="font-display font-bold uppercase text-sm">{lang === "fr" ? v.name_fr : v.name_en}</div>
                                <div className="text-[10px] font-mono-sf text-slate-500 mt-1">{v.preset}</div>
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2"><Music2 className="w-3.5 h-3.5" /> {lang === "fr" ? "Ambiance musique" : "Music mood"}</label>
                    <div className="mt-2 grid grid-cols-4 gap-2">
                        {MOODS.map((m) => (
                            <button key={m.id} onClick={() => setMood(m.id)}
                                className={`p-3 rounded border kinetic ${mood === m.id ? "border-[#FF4D00] bg-[#FF4D00]/10" : "border-[#253046] hover:border-[#3B4B6C]"}`}
                                style={{ borderTop: mood === m.id ? `2px solid ${m.color}` : undefined }}>
                                <div className="font-display font-bold uppercase text-sm">{m.label}</div>
                                <div className="text-[10px] font-mono-sf mt-1" style={{ color: m.color }}>{m.bpm} BPM</div>
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2"><Radio className="w-3.5 h-3.5" /> SFX layers</label>
                    <div className="mt-2 space-y-2">
                        {Object.entries(sfx).map(([k, on]) => (
                            <label key={k} className="flex items-center gap-3 p-2.5 rounded border border-[#253046] hover:border-[#3B4B6C] cursor-pointer kinetic">
                                <input type="checkbox" checked={on} onChange={(e) => setSfx({ ...sfx, [k]: e.target.checked })}
                                    className="w-4 h-4 accent-[#FF4D00]" />
                                <span className="font-mono-sf text-sm text-slate-200 uppercase">{k}</span>
                                <span className="ml-auto text-[10px] text-slate-500">{on ? "ON" : "OFF"}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            {/* Mix visualizer */}
            <div className="space-y-4">
                <div className="bg-gradient-to-b from-[#161D2A] to-[#0B111B] border border-[#253046] rounded-lg p-5">
                    <div className="flex items-center justify-between">
                        <div className="overline">Live mix</div>
                        <div className="flex items-center gap-2 text-[10px] font-mono-sf text-[#10B981]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] rec-dot" /> RENDERING
                        </div>
                    </div>

                    <div className="mt-5 space-y-4">
                        {[
                            { label: "Voice",  color: "#FF4D00", val: 0.85 },
                            { label: "Music",  color: "#0099FF", val: 0.55 },
                            { label: "SFX",    color: "#FFB800", val: 0.68 },
                            { label: "Master", color: "#10B981", val: 0.92 },
                        ].map((c) => (
                            <div key={c.label}>
                                <div className="flex justify-between text-[10px] font-mono-sf mb-1">
                                    <span className="text-slate-400">{c.label}</span>
                                    <span style={{ color: c.color }}>{Math.round(c.val * 100)}%</span>
                                </div>
                                <div className="h-2 rounded bg-[#0F141C] overflow-hidden">
                                    <div className="h-full transition-all" style={{ width: `${c.val * 100}%`, background: c.color }} />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6">
                        <div className="overline">Waveform preview</div>
                        <div className="mt-2 h-24 flex items-end gap-0.5">
                            {Array.from({ length: 60 }).map((_, i) => {
                                const h = 20 + Math.abs(Math.sin(i * 0.5 + voice.length)) * 80;
                                return <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, background: `linear-gradient(to top, #FF4D00, ${MOODS.find(m => m.id === mood)?.color || "#FFB800"})` }} />;
                            })}
                        </div>
                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-[#0F141C] border border-[#253046] rounded p-2.5">
                            <div className="overline">Voice</div>
                            <div className="text-slate-200 mt-1">{lang === "fr" ? VOICES.find(v => v.id === voice).name_fr : VOICES.find(v => v.id === voice).name_en}</div>
                        </div>
                        <div className="bg-[#0F141C] border border-[#253046] rounded p-2.5">
                            <div className="overline">Music</div>
                            <div className="text-slate-200 mt-1">{MOODS.find(m => m.id === mood).label} · {MOODS.find(m => m.id === mood).bpm} BPM</div>
                        </div>
                    </div>
                </div>

                <div className="bg-[#161D2A] border-l-2 border-[#FF4D00] rounded-r p-4">
                    <div className="overline flex items-center gap-2"><Wind className="w-3 h-3" /> Workflow</div>
                    <ol className="mt-2 text-sm text-slate-300 space-y-1 list-decimal list-inside">
                        <li>{lang === "fr" ? "Copie le preset voix vers ElevenLabs / Play.ht" : "Copy voice preset to ElevenLabs / Play.ht"}</li>
                        <li>{lang === "fr" ? "Télécharge la piste musicale sur Epidemic Sound" : "Download the track from Epidemic Sound"}</li>
                        <li>{lang === "fr" ? "Empile les SFX sélectionnés dans CapCut" : "Stack selected SFX in CapCut"}</li>
                        <li>{lang === "fr" ? "Applique le master à -14 LUFS pour YouTube" : "Master to -14 LUFS for YouTube"}</li>
                    </ol>
                </div>
            </div>
        </div>
    );
}
